import { FC, Fragment } from "react";
import {
    Box,
    Divider,
    Flex,
    Grid,
    GridItem,
    Icon,
    IconButton,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Text,
} from "@chakra-ui/react";
import { IconChevronRight, IconInfoCircle } from "@tabler/icons-react";
import { Shimmer } from "@am-tax/fe-core";
import { EntityResponse } from "../../api/models";
import { useClientEntities } from "../../api";

interface EntityInfoPopoverProps {
    clientId: string;
    entity: EntityResponse | undefined;
    isLoading: boolean;
}

/** Recursively searches the entity tree and returns the path from root to `targetId`. */
function findAncestorPath(entities: EntityResponse[], targetId: string, path: EntityResponse[] = []): EntityResponse[] | null {
    for (const entity of entities) {
        const currentPath = [...path, entity];
        if (entity.id === targetId) return currentPath;
        if (entity.children?.length) {
            const found = findAncestorPath(entity.children, targetId, currentPath);
            if (found) return found;
        }
    }
    return null;
}

export const EntityInfoPopover: FC<EntityInfoPopoverProps> = ({ clientId, entity, isLoading }) => {
    const entitiesQuery = useClientEntities(clientId);
    const ancestorPath = entity && entitiesQuery.data ? findAncestorPath(entitiesQuery.data, entity.id) : null;
    // Ancestors are all nodes except the last (which is the entity itself)
    const ancestors = ancestorPath && ancestorPath.length > 1 ? ancestorPath.slice(0, -1) : null;

    return (
        <Popover trigger="click" placement="bottom-start" isLazy>
            <PopoverTrigger>
                <IconButton icon={<Icon as={IconInfoCircle} />} aria-label="Entity details" variant="ghost" size="sm" display="inline-flex" />
            </PopoverTrigger>
            <PopoverContent minW={"18rem"} maxW={"30rem"}>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight={"semibold"}>Entity Details</PopoverHeader>
                <PopoverBody pb={4}>
                    <Shimmer isLoading={isLoading || entitiesQuery.isLoading}>
                        {/* Ancestor breadcrumb path */}
                        {ancestors && (
                            <>
                                <Flex align={"center"} flexWrap={"wrap"} gap={".1rem"} mb={".5rem"} color={"gray.500"} fontSize={"sm"}>
                                    {ancestors.map((ancestor, i) => (
                                        <Fragment key={ancestor.id}>
                                            {i > 0 && <Icon as={IconChevronRight} boxSize={"0.85em"} />}
                                            <Text as="span">{ancestor.name}</Text>
                                        </Fragment>
                                    ))}
                                    <Icon as={IconChevronRight} boxSize={"0.85em"} />
                                    <Text as="span" fontWeight={"semibold"} color={"gray.700"}>
                                        {entity?.name}
                                    </Text>
                                </Flex>
                                <Divider mb={".5rem"} />
                            </>
                        )}

                        <Grid templateColumns={"auto 1fr"} columnGap={"1rem"} rowGap={".3rem"} alignItems={"baseline"}>
                            <EntityRow label="Name" value={entity?.name} />
                            <EntityRow label="Type" value={entity?.entityType === "Leaf" ? "Location" : "Folder"} />
                            {entity?.externalId && <EntityRow label="External ID" value={entity.externalId} />}
                            {entity?.ein && <EntityRow label="EIN" value={entity.ein} />}
                            {entity?.phone && <EntityRow label="Phone" value={entity.phone} />}
                            {(entity?.street1 || entity?.city) && (
                                <>
                                    <GridItem colSpan={2}>
                                        <Divider my={".3rem"} />
                                    </GridItem>
                                    <GridItem>
                                        <Text variant={"label"} whiteSpace={"nowrap"}>
                                            Address:
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Box>
                                            {entity?.street1 && <Text>{entity.street1}</Text>}
                                            {entity?.street2 && <Text>{entity.street2}</Text>}
                                            {(entity?.city || entity?.state || entity?.zipCode) && (
                                                <Text>{[entity?.city, entity?.state, entity?.zipCode].filter(Boolean).join(", ")}</Text>
                                            )}
                                        </Box>
                                    </GridItem>
                                </>
                            )}
                            {entity?.note && (
                                <>
                                    <GridItem colSpan={2}>
                                        <Divider my={".3rem"} />
                                    </GridItem>
                                    <GridItem>
                                        <Text variant={"label"}>Note:</Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text>{entity.note}</Text>
                                    </GridItem>
                                </>
                            )}
                        </Grid>
                    </Shimmer>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

const EntityRow: FC<{ label: string; value: string | undefined }> = ({ label, value }) => (
    <>
        <GridItem>
            <Text variant={"label"} whiteSpace={"nowrap"}>
                {label}:
            </Text>
        </GridItem>
        <GridItem>
            <Text>
                {value ?? (
                    <Text as="span" color={"gray.400"}>
                        —
                    </Text>
                )}
            </Text>
        </GridItem>
    </>
);
